import { useRef, useEffect } from 'react'
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react'
import SEO from '../components/SEO'
import { useApp } from '../context/AppContext'
import { t } from '../i18n'

const WHATSAPP = 'https://wa.me/201028137221'
const PHONE_HREF = 'tel:+201028137221'

interface ContactItem {
  icon: React.ReactNode
  label: string
  value?: string
  href?: string
  lines?: { text: string; href: string }[]
}

export default function Contact() {
  const { lang } = useApp()
  const isAr = lang === 'ar'

  const branches = [
    { name: isAr ? 'كفر الشيخ, مصر' : 'Kafr El-Sheikh, Egypt', sub: isAr ? 'هيئة قضاة الدولة، 33511' : 'State Judges Authority, 33511', href: 'https://maps.google.com/?q=هيئة+قضاة+الدولة+كفر+الشيخ+مصر' },
    { name: isAr ? 'أكتوبر, الجيزة، مصر' : 'October, Giza, Egypt', sub: isAr ? 'مدينة السادس من أكتوبر' : '6th of October City', href: 'https://maps.google.com/?q=السادس+من+أكتوبر+الجيزة+مصر' },
    { name: isAr ? 'أركان التميز, السعودية' : 'Arkan Al-Tamayoz, Saudi Arabia', sub: isAr ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Saudi Arabia', href: 'https://maps.google.com/?q=أركان+التميز+السعودية' },
    { name: isAr ? 'توب بيراميدز, البحرين' : 'Top Pyramids, Bahrain', sub: isAr ? 'المنامة، مملكة البحرين' : 'Manama, Bahrain', href: 'https://maps.google.com/?q=توب+بيراميدز+البحرين' },
  ]

  const contactItems: ContactItem[] = [
    { icon: <Phone size={20} aria-label={t(lang, 'phone')} />, label: t(lang, 'phone'), lines: [
      { text: '+20 102 813 7221', href: 'tel:+201028137221' },
      { text: '+966 55 746 6681', href: 'tel:+966557466681' },
      { text: '+966 50 594 7475', href: 'tel:+966505947475' },
    ]},
    { icon: <Mail size={20} aria-label={t(lang, 'email')} />, label: t(lang, 'email'), lines: [
      { text: 'shelillogistics@gmail.com', href: 'mailto:shelillogistics@gmail.com' },
      { text: 'info@ercanaltamiz.com', href: 'mailto:info@ercanaltamiz.com' },
    ]},
    { icon: <MapPin size={20} aria-label={isAr ? 'فروعنا' : 'Our Branches'} />, label: isAr ? 'فروعنا' : 'Our Branches', lines: [
      { text: isAr ? 'كفر الشيخ, هيئة قضاة الدولة، مصر 33511' : 'Kafr El-Sheikh, State Judges Authority, Egypt 33511', href: 'https://maps.google.com/?q=هيئة+قضاة+الدولة+كفر+الشيخ+مصر' },
      { text: isAr ? 'أكتوبر, مدينة السادس من أكتوبر، الجيزة' : 'October, 6th of October City, Giza', href: 'https://maps.google.com/?q=السادس+من+أكتوبر+الجيزة+مصر' },
      { text: isAr ? 'السعودية, أركان التميز' : 'Saudi Arabia, Arkan Al-Tamayoz', href: 'https://maps.google.com/?q=أركان+التميز+السعودية' },
      { text: isAr ? 'البحرين, توب بيراميدز' : 'Bahrain, Top Pyramids', href: 'https://maps.google.com/?q=توب+بيراميدز+البحرين' },
    ]},
    { icon: <Clock size={20} aria-label={isAr ? 'ساعات العمل' : 'Working Hours'} />, label: isAr ? 'ساعات العمل' : 'Working Hours', value: isAr ? 'السبت - الخميس: 9ص - 6م' : 'Sat - Thu: 9AM - 6PM', href: '#' },
  ]

  const mapRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = mapRef.current
    if (!el) return
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('loaded'); obs.disconnect() } }, { threshold: 0.15 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const ctaRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ctaRef.current
    if (!el) return
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('anim-visible'); obs.disconnect() } }, { threshold: 0.2 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <main className="page contact-page" dir={isAr ? 'rtl' : 'ltr'}>
      <SEO
        title={isAr ? 'تواصل معنا' : 'Contact Us'}
        description={isAr ? 'تواصل مع شليل للشحن البري - استشارة مجانية وخدمات الشحن البري والتخليص الجمركي.' : 'Contact Shalil Land Freight - free consultation, land freight and customs clearance services.'}
        url="https://shelil-logistics-services.pages.dev/contact"
      />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-bg">
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <p className="hero-tagline">{t(lang, 'contactHeroTitle')}</p>
          <h1 className="hero-title">{t(lang, 'contactHeroTitle')}</h1>
          <p className="hero-desc">{t(lang, 'contactHeroDesc')}</p>
          <div className="hero-btns">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="btn btn-primary">{isAr ? 'ابدأ محادثة' : 'Start a Chat'}</a>
            <a href={PHONE_HREF} className="btn btn-outline">{isAr ? 'اتصل بنا' : 'Call Us'}</a>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{isAr ? 'طرق التواصل' : 'Contact Methods'}</h2>
            <p>{isAr ? 'فريقنا يرد على مدار الساعة.' : 'Our team replies 24/7.'}</p>
          </div>
          <div className="services-grid">
            {contactItems.map((item, i) => (
              <div key={i} className="service-card anim-child">
                <div className="service-icon">{item.icon}</div>
                <h3>{item.label}</h3>
                {item.lines ? (
                  item.lines.map((line, j) => (
                    <a key={j} href={line.href} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--text-muted)', textDecoration: 'none', marginBottom: 8 }} dir={line.href.startsWith('tel:') ? 'ltr' : undefined}>
                      {line.text}
                    </a>
                  ))
                ) : (
                  <span style={{ display: 'block', color: 'var(--text-muted)' }} dir={item.href === PHONE_HREF ? 'ltr' : undefined}>{item.value}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="section-inner">
          <div className="section-header">
            <h2>{isAr ? 'موقعنا' : 'Our Locations'}</h2>
          </div>
          <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border)', minHeight: '520px', background: 'var(--bg-section)', position: 'relative' }}>
            <div className="map-placeholder" id="map-placeholder">
              <div className="map-placeholder-icon"><MapPin size={32} /></div>
              <p className="map-placeholder-text">{isAr ? 'جارٍ تحميل الخريطة...' : 'Loading map...'}</p>
            </div>
            <iframe
              title="branch locations"
              src="https://maps.google.com/maps?q=30.0444,31.2357(Cairo)&t=&z=6&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: 'var(--radius-lg)', display: 'block', minHeight: '520px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => {
                const el = document.getElementById('map-placeholder')
                if (el) el.classList.add('loaded')
              }}
            />
          </div>
          <div className="services-grid" style={{ marginTop: 32 }}>
            {branches.map((b, i) => (
              <div key={i} className="service-card anim-child">
                <div className="service-icon"><MapPin size={16} /></div>
                <h3>{b.name}</h3>
                <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{b.sub}</p>
                <a href={b.href} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 14, fontWeight: 600 }}>{isAr ? 'افتح على الخريطة' : 'Open on Map'}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" ref={ctaRef}>
        <div className="cta-inner">
          <div className="service-icon" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--accent)', width: 64, height: 64, fontSize: 32, margin: '0 auto 20px' }}><MessageCircle size={40} /></div>
          <h2 style={{ color: '#fff', fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, marginBottom: 16 }}>{isAr ? 'تواصل معنا مباشرة على واتساب' : 'Chat with us on WhatsApp'}</h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 17, marginBottom: 32 }}>{isAr ? 'فريقنا يرد على مدار الساعة.' : 'Our team replies 24/7.'}</p>
          <div className="hero-btns">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="btn btn-primary">{isAr ? 'ابدأ محادثة' : 'Start a Chat'}</a>
            <a href={PHONE_HREF} className="btn btn-outline">{isAr ? 'اتصل بنا' : 'Call Us'}</a>
          </div>
        </div>
      </section>
    </main>
  )
}
